import KayakHero from 'assets/images/psp/PSPHero/kayak-hero.webp';
import GarageSpace from 'assets/images/psp/Icon/garage-space.svg';
import ImproperSupport from 'assets/images/psp/Icon/improper-support.svg';
import SeasonalClutter from 'assets/images/psp/Icon/seasonal-clutter.svg';
import UVWeatherDamage from 'assets/images/psp/Icon/uv-weather-damage.svg';
import SecurityConcerns from 'assets/images/psp/Icon/security-concerns.svg';
import AccessibilityIssues from 'assets/images/psp/Icon/accessibility-issues.svg';
import SizeComparisonKayak from 'assets/images/psp/Icon/kayak.svg';
import MaximizeSpace from 'assets/images/psp/Icon/maximize-space.svg';
import OrganizeSeason from 'assets/images/psp/Icon/organize-season.svg';
import ProtectInvestment from 'assets/images/psp/Icon/protect-investment.svg';
import PlanGrowth from 'assets/images/psp/Icon/plan-growth.svg';
import { storageCategories } from 'pages/PSPPage/utils/getPSPLinks';

const kayakContentPrepMarkdown =
	'Smart storage for kayaks starts with understanding your space needs Here`s how to determine the perfect unit size for your watercraft collection:\n\n' +
	'### Kayak and Canoe Dimensions Guide\n\n' +
	'* **Recreational Kayaks:** 9-12 feet long, 28-36 inches wide\n\n ' +
	'* **Touring Kayaks:** 12-18 feet long, 20-24 inches wide\n\n' +
	'* **Canoes:** 15-18 feet long, 33-37 inches wide\n\n' +
	'* **Fishing Kayaks:** 10-14 feet long, 32-40 inches wide\n\n';
const kayakSizeRecommendation =
	'### Storage Unit Size Recommendations \n\n ' +
	'**5x10 Unit - Solo Paddler Storage**\n\n ' +
	'> Perfect for 1-2 kayaks with vertical kayak storage, plus paddles, life jackets, and safety gear.\n\n ' +
	'**5x15 Unit - Small Family Fleet**\n\n ' +
	'> Accommodates 2-3 kayaks or canoes with room for all paddling accessories and seasonal gear.\n\n ' +
	'**10x10 Unit - Multi-Boat Storage**\n\n> Ideal for larger families with multiple watercraft, offering horizontal and vertical storage options\n\n ' +
	'**10x15 Unit - Complete Paddling Setup**\n\n ' +
	'> Premium space for extensive gear collections, multiple boats, and comfortable access for maintenance.\n\n ' +
	'**Storage tip:** Vertical kayak storage maximizes space efficiency, allowing you to store more boats in smaller units while maintaining proper hull support.\n\n';
const kayakStorageCostBreakdownMarkdown =
	'### Kayak Storage Cost Breakdown for Budget-Conscious Paddlers \n\n' +
	'Professional canoe and kayak storage costs far less than alternatives such as building a canoe storage shed in your backyard. Building a boat house or garage addition runs $5,000-$20,000, while replacing weather-damaged compact watercraft costs $500-$3,000 per boat.\n\n' +
	'Storage facilities offer budget-friendly protection with the flexibility to adjust as your paddling hobby grows.\n\n' +
	'Whether you need winter storage or year-round organization, flexible month-to-month options let you pay only for what you use.';
const KayakStorageContentPrepMarkdown =
	'1. **Proper Hull Support Systems**\n\n' +
	'   Professional facilities understand watercraft storage requirements, providing proper support that prevents hull deformation and stress cracks.\n\n' +
	'2. **Climate Protection**\n\n' +
	'   Controlled environments prevent the UV damage, temperature extremes, and moisture issues that plague home storage solutions.\n\n' +
	'3. **Organized Gear Management**\n\n' +
	'   Dedicated space for paddles, safety equipment, fishing gear, and seasonal accessories keeps everything organized and accessible.\n\n' +
	'4. **Convenient Location Access**\n\n' +
	'   Many facilities offer extended hours and easy loading access, making it simple to grab your gear for impromptu paddling trips.\n\n' +
	'5. **Insurance and Security Peace of Mind**\n\n' +
	'   Professional-grade security systems and proper insurance coverage protect your investment better than home storage options.';
const reviews = [
	{
		title: 'Excellent Service',
		stars: 5,
		text: 'I live in the NC mountains and contacted SelfStorage.com about a self storage unit for my ocean kayak. They quickly found me a 10x15 unit that met my needs, at the price I wanted. I was able to reserve my unit online, and the facility contacted me that same day with my reservation number. Great service!',
		customerName: 'Daniel',
		date: 'Jan 20, 2014',
		link: 'https://www.trustpilot.com/users/52dd649700006400015f7aaf',
	},
	{
		title: 'All your options incredibly easy',
		stars: 5,
		text: 'While reviewing individual places online I found a site that`s like the Kayak of storage centers. It was very straightforward to find all the options I needed in a unit, and the online deal I found is saving me about $30 a month on my 5x10.',
		customerName: 'Paul',
		date: 'Dec 23, 2013',
		link: 'https://www.trustpilot.com/reviews/52b7da8b00006400026c75b0',
	},
	{
		title: 'Great service',
		stars: 5,
		text: 'I had to relocate my boat at short notice. Angie was very helpful in helping me to resolve that issue. I was very satisfied with the accommodation of space at the facility we found. This location meets all of my storage and space needs. ',
		customerName: 'Stafford Bailey',
		date: 'Apr 23, 2023',
		link: 'https://www.trustpilot.com/reviews/64454a74746ca7dfea0f07e9',
	},
	{
		title: 'This service is convenient and easy to use',
		stars: 5,
		text: 'The site provided the info I needed to find boat storage in my area at a reasonable rate. I was then able to reserve a space immediately before actually talking with anyone. This service is convenient and easy to use.',
		customerName: 'Steve K',
		date: 'Oct 17, 2021',
		link: 'https://www.trustpilot.com/reviews/616b6e80234f1b1fe55c3604',
	},
];
const faqs = [
	{
		question: 'Can I use vertical kayak storage in a regular storage unit?',
		answer: 'Many facilities offer or allow vertical storage racks, which maximize space efficiency. Check with individual facilities about their vertical kayak storage policies and available rack systems.',
	},
	{
		question: "What's the minimum storage unit size for two kayaks?",
		answer: 'A 5x10 unit typically accommodates two recreational kayaks with vertical storage. For horizontal storage or longer touring kayaks, consider a 5x15 or 10x10 unit.',
	},
	{
		question: 'How should I prepare my watercraft for long-term storage?',
		answer: "Clean thoroughly, ensure they're completely dry, remove any electronic equipment, and store them in a well-ventilated area. Professional facilities often provide preparation guidelines.",
	},
	{
		question: 'Can I store paddles and gear with my boats?',
		answer: 'Absolutely! Most paddlers store their complete setup, including paddles, life jackets, safety equipment, and fishing gear, alongside their watercraft.',
	},
	{
		question:
			'Is climate controlled storage necessary for kayaks and canoes?',
		answer: 'While not always essential, climate control prevents extreme temperature fluctuations that can stress plastic and composite hulls, especially for high-end watercraft.',
	},
	{
		question: 'How do I transport kayaks to and from storage?',
		answer: 'Most storage facilities have wide driveways and easy loading access. Bring your roof rack system or trailer - many paddlers find storage actually makes transport easier.',
	},
	{
		question: 'What about winter storage for my paddling gear?',
		answer: 'Winter storage in a professional facility prevents freeze damage to gear and keeps everything organized for spring. Many paddlers use seasonal storage contracts.',
	},
	{
		question: 'Can I access my kayaks anytime I want to paddle?',
		answer: 'Most canoe and kayak storage facilities offer extended access hours, with many providing daily access during reasonable hours for spontaneous paddling adventures.',
	},
];

export const KayakContent = {
	type: 'Kayak',
	reviews: reviews,
	sections: [
		{
			title: 'Kayak Storage Hero',
			background: 'default',
			modules: [
				{
					type: 'hero',
					id: 'Kayak-storage-hero',
					props: {
						content: {
							title: 'Find Affordable Kayak Self Storage',
							description:
								'Does finding the right at-home storage space for your kayak or canoe feel like you’re paddling upstream? Keep that garage tidy and [shed organized](https://www.selfstorage.com/blog/how-to-organize-a-shed-to-maximize-storage-space-2/) when you choose self storage for kayak storage. \n\n' +
								'Whether you own one kayak or an entire fleet of watercraft for your family or business, storage facilities offer the perfect solution for seasonal storage, gear organization, and protecting your boats from damage.\n\n' +
								'Use **SelfStorage.com** to say goodbye to awkward garage storage and hello to convenient, secure protection for all your paddling adventures.\n\n',
							image: KayakHero,
							CTAtext: 'Find Kayak Self Storage',
							storageType: 'Kayak',
						},
					},
				},
			],
		},
		{
			title: 'unit size chart',
			modules: [
				{
					type: 'cardStrip',
					props: {
						heading:
							'The Kayak Storage Challenge Every Paddler Faces',
						description:
							'Your kayaks and canoes represent freedom on the water and family adventure time, but storing them at home has become a constant struggle. \n\n Garage rafters sag under the weight, basement storage creates humidity problems, and outdoor storage leaves your investment vulnerable to weather damage and theft.',
						cardData: [
							{
								content:
									'**Garage Space Overload** \n\n Kayaks consume precious car and storage space\n\n → **Dedicated units** designed specifically for watercraft dimensions',
								imageSrc: GarageSpace,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Improper Support** \n\n Wall mounts and rafters stress hulls and cause warping, even in vertical kayak storage scenarios\n\n → **Professional racking** with proper hull support and vertical kayak storage options',
								imageSrc: ImproperSupport,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Seasonal Clutter** \n\n Multiple kayaks, paddles, and gear take over living spaces\n\n → **Organized storage** with room for all your paddling equipment in one place',
								imageSrc: SeasonalClutter,
								contentAlign: 'text-left',
							},
							{
								content:
									'**UV and Weather Damage**\n\n' +
									'Outdoor storage causes fading, cracking, and deterioration\n\n ' +
									'→ **Indoor protection** shields from sun, rain, and temperature extremes',
								imageSrc: UVWeatherDamage,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Security Concerns**\n\n' +
									'Kayaks are attractive targets for thieves when stored visibly\n\n ' +
									'→ **Gated facilities** with surveillance and controlled access systems',
								imageSrc: SecurityConcerns,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Accessibility Issues** \n\n' +
									'Hard-to-reach storage makes spontaneous paddling trips impossible\n\n ' +
									'→ **Easy access storage** lets you grab your gear and go when adventure calls',
								imageSrc: AccessibilityIssues,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},
		{
			title: 'content section ',
			background: 'blueWithMap',
			modules: [
				{
					type: 'content',
					props: {
						title: 'Choosing the Right Storage Size for Your Fleet',
						contentType: 'row-type',
						theme: 'dark',
						content: [
							kayakContentPrepMarkdown,
							kayakSizeRecommendation,
							kayakStorageCostBreakdownMarkdown,
						],
					},
				},
			],
		},
		{
			title: 'Size Comparison Section 1',
			background: 'lighterBlue',
			modules: [
				{
					type: 'sizeComparisonSection',
					props: {
						content: {
							image: SizeComparisonKayak,
							contentdec:
								'*Pricing reflects data from **SelfStorage.com**, with costs varying by location. Smaller units perfect for storage for kayaks start from very affordable monthly rates.*',
						},

						storageData: [
							{
								size: '5x10',
								desc: [
									'**Monthly Cost:** $35-$85*',
									'**Perfect For:** 1-2 kayaks, seasonal storage',
									'**Storage Benefits:** Affordable protection, secure access',
								],
							},
							{
								size: '5x15',
								desc: [
									'**Monthly Cost:** $45-$110*',
									'**Perfect For:** Small families, moderate gear storage',
									'**Storage Benefits:** Room for multiple boats and equipment',
								],
							},
							{
								size: '10x10',
								desc: [
									'**Monthly Cost:** $65-$140*',
									'**Perfect For:** Serious paddlers, [large collections](https://www.selfstorage.com/blog/outdoor-gear-shed-storage-unit/)',
									'**Storage Benefits:** Vertical and horizontal storage options',
								],
							},
							{
								size: '10x15',
								desc: [
									'**Monthly Cost:** $85-$175*',
									'**Perfect For:** Kayak clubs, extensive gear, maintenance space',
									'**Storage Benefits:** Maximum flexibility and organization',
								],
							},
						],
					},
				},
			],
		},

		{
			title: 'content section 2',
			background: 'blueWithMap',
			modules: [
				{
					type: 'content',
					props: {
						title: '5 Reasons to Choose Self Storage for Canoe & Kayak Storage',
						contentType: 'row-type',
						theme: 'dark',
						content: [KayakStorageContentPrepMarkdown],
					},
				},
			],
		},

		{
			title: 'unit size chart 1',
			modules: [
				{
					type: 'cardStrip',
					props: {
						heading:
							'Quick Storage Tips for Kayaks, Canoes, and Compact Watercraft',
						cardData: [
							{
								title: 'Maximize Your Space',
								content:
									'Use vertical kayak storage when possible, store paddles along walls, and utilize overhead space for lightweight gear like life jackets.',
								imageSrc: MaximizeSpace,
								contentAlign: 'text-left',
							},
							{
								title: 'Organize by Season',
								content:
									'Group summer gear together, separate fishing equipment from touring accessories, and keep safety items easily accessible.',
								imageSrc: OrganizeSeason,
								contentAlign: 'text-left',
							},
							{
								title: 'Protect Your Investment',
								content:
									'Even in storage, use boat covers for additional protection, store batteries separately from fish finders, and check on your gear periodically.',
								imageSrc: ProtectInvestment,
								contentAlign: 'text-left',
							},
							{
								title: 'Plan for Growth',
								content:
									'Choose storage for kayaks with room to expand - most paddling enthusiasts add boats and gear over time as their hobby develops.',
								imageSrc: PlanGrowth,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},

		{
			title: 'Reviews Section',
			background: 'lighterBlue',
			modules: [
				{
					type: 'reviews',
					slug: 'Kayak-reviews',
					props: {
						reviewsData: {
							reviews,
							headerTitle: 'Kayak Self Storage: ',
						},
					},
				},
			],
		},
		{
			title: 'Faqs Section',
			background: 'white',
			modules: [
				{
					type: 'faqs',
					props: {
						content: {
							header: 'Kayak Self Storage FAQs',
							faqs,
						},
					},
				},
			],
		},
		{
			title: 'featured content',
			background: 'darkBlue',
			modules: [
				{
					type: 'featuredContent',
					props: {
						data: {
							heading: 'Learn All About Kayak Self Storage',
							type: 'unitCards',
							id: 'storage size unit cards',
							data: {
								slug: 'storage-size-guide-unit-cards',
								unitCards: [
									{
										title: 'Vehicle Storage',
										date: 'October 16, 2025',
										layout: false,
										plain: true,
										image: 'https://www.selfstorage.com/blog/wp-content/uploads/2025/10/motorcycle.png',
										content:
											'Small Vehicle Storage: How to Store Motorcycles, ATVs, and Golf Carts ',
										url: 'https://www.selfstorage.com/blog/small-vehicle-storage-guide/',
									},
									{
										title: 'Vehicle Storage',
										date: 'July 23, 2025',
										layout: false,
										plain: true,
										image: 'https://www.selfstorage.com/blog/wp-content/uploads/2022/07/dry-stack-boat-storage.jpg',
										content:
											'Best Way to Store a Boat During Off-Season',
										url: 'https://www.selfstorage.com/blog/how-to-store-a-boat-2',
									},
									{
										title: 'Storage Tips',
										date: 'October 16, 2025',
										layout: false,
										plain: true,
										image: 'https://www.selfstorage.com/blog/wp-content/uploads/2025/05/shutterstock_209402077-scaled.jpg',
										content:
											'Sports Equipment Storage Guide: How to Create A Sports and Outdoor Gear Storage Room',
										url: 'https://www.selfstorage.com/blog/outdoor-gear-shed-storage-unit/',
									},
									{
										title: 'Storage Tips',
										date: 'May 14, 2025',
										layout: false,
										plain: true,
										image: 'https://www.selfstorage.com/blog/wp-content/uploads/2025/04/shutterstock_1548283061-scaled.jpg',
										content:
											'How to Prepare & Store Hiking and Camping Gear | Storage Guide',
										url: 'https://www.selfstorage.com/blog/preparing-hiking-equipment-for-storage/',
									},
								],
							},
						},
					},
				},
			],
		},
		{
			title: 'Links to other PSPs',
			background: 'lighterBlue',
			modules: [
				{
					type: 'links',
					props: {
						heading: {
							headingName: 'More Ways to Store',
						},
						linksList: storageCategories.kayak,
					},
				},
			],
		},
		{
			title: 'nearby units',
			modules: [
				{
					type: 'storageNearYou',
					props: {
						type: 'Kayak',
					},
				},
			],
		},
	],
};
